import { useCallback, useEffect, useRef, useState } from "react";
import type { CollapsedChatMode } from "./CollapsedChatBar";
import { latestAssistantReply, type AssistantReplySummary } from "./collapsedReplyPreview";
import { shouldShowUnreadReply } from "./shouldShowUnreadReply";

interface UnreadReply {
  threadId: string | null;
  reply: AssistantReplySummary;
}

interface CollapsedReplyState {
  mode: CollapsedChatMode;
  replyPreview: string | null;
  scrollToLatestReplyRequest: number;
  clearUnreadReply: () => void;
  openLatestReply: () => void;
}

interface CollapsedReplyParams {
  messages: Parameters<typeof latestAssistantReply>[0];
  isLoading: boolean;
  isCollapsed: boolean;
  threadId: string | null;
}

/** Tracks a response completed while the panel is collapsed until it is opened. */
export function useCollapsedReplyState(params: CollapsedReplyParams): CollapsedReplyState {
  const { messages, isLoading, isCollapsed, threadId } = params;
  const latestReply = latestAssistantReply(messages);
  const [unreadReply, setUnreadReply] = useState<UnreadReply | null>(null);
  const [scrollToLatestReplyRequest, setScrollToLatestReplyRequest] = useState(0);
  const wasLoadingRef = useRef(false);
  const loadingThreadRef = useRef<string | null>(null);
  const replyBeforeLoadingRef = useRef<string | null>(latestReply?.key ?? null);
  useEffect(() => {
    if (isLoading) {
      if (!wasLoadingRef.current) {
        wasLoadingRef.current = true;
        replyBeforeLoadingRef.current = latestReply?.key ?? null;
        setUnreadReply(null);
      }
      loadingThreadRef.current = threadId;
      return;
    }
    if (!wasLoadingRef.current) return;
    wasLoadingRef.current = false;
    const completedThreadId = loadingThreadRef.current;
    loadingThreadRef.current = null;
    if (
      shouldShowUnreadReply({
        reply: latestReply,
        replyBeforeLoading: replyBeforeLoadingRef.current,
        completedThreadId,
        currentThreadId: threadId,
        isCollapsed,
      })
    ) {
      setUnreadReply({ threadId, reply: latestReply });
    }
  }, [isCollapsed, isLoading, latestReply, threadId]);
  const clearUnreadReply = useCallback(() => setUnreadReply(null), []);
  const openLatestReply = useCallback(() => {
    clearUnreadReply();
    setScrollToLatestReplyRequest((request) => request + 1);
  }, [clearUnreadReply]);
  const visibleReply = unreadReply?.threadId === threadId ? unreadReply.reply : null;
  return {
    mode: isLoading ? "working" : visibleReply ? "reply" : "idle",
    replyPreview: visibleReply?.preview ?? null,
    scrollToLatestReplyRequest,
    clearUnreadReply,
    openLatestReply,
  };
}
